/**
 * HomeBlocksLayout — تخطيط بلوكات الهوم القابل لإعادة الاستخدام.
 *
 * تستخدمه الصفحة الرئيسيّة فقط — مصدر إعدادات واحد في app_settings.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight } from "lucide-react";
import * as React from "react";
import { island } from "@/lib/lazyIsland";
import { useLocale } from "@/contexts/LocaleContext";
import { AutoMedia } from "@/components/AutoMedia";
import { TEXT_POSITION_CLASSES, normalizeTextPosition } from "@/lib/text-position";
import { cleanBlockText, renderBoldText } from "@/lib/block-text";
import { HomeHero } from "@/components/HomeHero";
import { HomeSection } from "@/components/home/HomeSection";
import { HomeDesktopEditorial } from "@/components/home/HomeDesktopEditorial";
import { BlockDeviceGate } from "@/components/home/BlockDeviceGate";
import { HomeBlockOpacityWrap } from "@/components/HomeBlockOpacityWrap";

import { HOME_BLOCK_KEYS, type HomeBlockKey } from "@/lib/home-block-keys";
import { getHomeAboutBlock } from "@/utils/homeAboutBlock.functions";
import { softCtaClass } from "@/components/ui/soft-cta";
import { getHomeTexts } from "@/utils/homeTexts.functions";

// ⚡ بلوكات أسفل الشاشة الأولى: خارج حزمة الإقلاع (تُحمَّل بعد أوّل رسم)
const HomeStoryBanner = island(() => import("@/components/HomeStoryBanner"), "HomeStoryBanner");
const HomeQuoteVideoBlock = island(() => import("@/components/HomeQuoteVideoBlock"), "HomeQuoteVideoBlock");
const HomeEditorialNote = island(() => import("@/components/HomeEditorialNote"), "HomeEditorialNote");
const HomeShutterReveal = island(() => import("@/components/HomeShutterReveal"), "HomeShutterReveal");
const HomeSoftBanner = island(() => import("@/components/HomeSoftBanner"), "HomeSoftBanner");
const HomeSixGrid = island(() => import("@/components/HomeSixGrid"), "HomeSixGrid");
// ⚡ أدمن فقط (شارة التعديل اليدويّ): خارج حزمة الزائر
const BlockOpacityQuickEdit = island(() => import("@/components/home/BlockOpacityQuickEdit"), "BlockOpacityQuickEdit");




type BlockMeta = {
  number: number;
  name: string;
  arName: string;
  editHref?: string;
  note?: string;
  /** فئات إظهار الشارة حسب الجهاز (البلوك نفسه مخفيّ على الجهة الأخرى) */
  labelDeviceClass?: string;
};

const HOME_BLOCKS: Record<string, BlockMeta> = {
  HomeHero: {
    number: 1,
    name: "HomeHero",
    arName: "بلوك ١",
    editHref: "/admin/page-backgrounds?tab=hero",
  },
  AboutIntro: {
    number: 2,
    name: "AboutIntro",
    arName: "بلوك ٢",
    editHref: "/admin/home-about#media",
  },
  AboutIntroTwo: {
    number: 3,
    name: "AboutIntroTwo",
    arName: "بلوك ٣",
    editHref: "/admin/home-about?slot=secondary#media",
  },
  AboutIntroThree: {
    number: 4,
    name: "AboutIntroThree",
    arName: "بلوك ٤",
    editHref: "/admin/home-about?slot=tertiary#media",
  },
  HomeSoftBanner: {
    number: 5,
    name: "HomeSoftBanner",
    arName: "بلوك ٥",
    editHref: "/admin/home-soft-banner",
    labelDeviceClass: "md:hidden",
  },
  HomeSixGrid: {
    number: 5,
    name: "HomeSixGrid",
    arName: "بلوك ٥",
    editHref: "/admin/home-six-grid",
    labelDeviceClass: "hidden md:flex",
  },


  HomeEditorialNote: {
    number: 6,
    name: "HomeEditorialNote",
    arName: "بلوك ٦",
    editHref: "/admin/home-editorial-note",
  },
  HomeQuoteVideoBlock: {
    number: 7,
    name: "HomeQuoteVideoBlock",
    arName: "بلوك ٧",
    editHref: "/admin/home-quote-video#media",
  },
  HomeStoryBanner: {
    number: 8,
    name: "HomeStoryBanner",
    arName: "بلوك ٨",
    editHref: "/admin/home-story-banner#media",
  },
  NemroQuoteBlock: {
    number: 9,
    name: "NemroQuoteBlock",
    arName: "بلوك ٩",
    editHref: "/admin/home-texts#quote",
  },
};

function LabeledBlock({
  blockKey,
  children,
  showLabel,
}: {
  blockKey: string;
  children: React.ReactNode;
  showLabel: boolean;
}) {
  const meta = HOME_BLOCKS[blockKey];
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = () => setVisible(el.offsetHeight > 60);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showLabel]);

  if (!meta || !showLabel) return <>{children}</>;
  return (
    <div ref={hostRef} className="relative" data-block={meta.number} data-block-name={meta.name}>
      {children}
      <div className={`pointer-events-auto absolute ${blockKey === "HomeHero" ? "top-24" : "top-3"} left-3 z-[9999] ${visible ? (meta.labelDeviceClass ?? "flex") : "hidden"} max-w-[calc(100%-24px)] flex-wrap items-center gap-2 rounded-none border border-border bg-foreground px-2.5 py-1.5 text-background shadow-elegant`}>

        <span className="flex h-5 min-w-[20px] items-center justify-center bg-background text-[10px] font-bold text-foreground">
          {meta.number}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-background/90">
          {meta.name}
        </span>
        <span className="text-[10px] text-background/70">· {meta.arName}</span>
        {meta.editHref ? (
          <a
            href={meta.editHref}
            className="flex min-h-[36px] items-center text-[10px] underline underline-offset-2 hover:no-underline"
          >
            تعديل
          </a>
        ) : (
          <span className="text-[10px] text-background/50">{meta.note}</span>
        )}
        {(HOME_BLOCK_KEYS as readonly string[]).includes(blockKey) ? (
          <React.Suspense fallback={null}>
            <BlockOpacityQuickEdit blockKey={blockKey as HomeBlockKey} />
          </React.Suspense>
        ) : null}


      </div>
    </div>
  );
}

/**
 * تفعيل أرقام البلوكات:
 *  - تلقائيّاً في وضع التطوير
 *  - أو بإضافة `?blocks=1` إلى الرابط (تُحفظ في localStorage) و `?blocks=0` للإخفاء
 */
const BLOCKS_LS_KEY = "lecmo:home-blocks";

function readBlocksFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get("blocks");
    if (q === "1") {
      window.localStorage.setItem(BLOCKS_LS_KEY, "1");
      return true;
    }
    if (q === "0") {
      window.localStorage.removeItem(BLOCKS_LS_KEY);
      return false;
    }
    return window.localStorage.getItem(BLOCKS_LS_KEY) === "1";
  } catch {
    return false;
  }
}

export function HomeBlocksLayout({
  initialHero,
}: {
  /** ⚡ LCP: صورة الهيرو من loader الصفحة الرئيسيّة. */
  initialHero?: { mobileImage?: string | null; desktopImage?: string | null } | null;
} = {}) {
  const [showLabels, setShowLabels] = React.useState(import.meta.env.DEV);
  const [enabled, setEnabled] = React.useState(import.meta.env.DEV);
  React.useEffect(() => {
    const flag = readBlocksFlag();
    if (flag) {
      setEnabled(true);
      setShowLabels(true);
    }
  }, []);
  const isDev = enabled;

  return (
    <>
      {isDev && (
        <button
          type="button"
          onClick={() => setShowLabels((s) => !s)}
          className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 rounded-none border border-border bg-foreground px-3 py-2 text-[10px] uppercase tracking-wider text-background shadow-elegant hover:bg-foreground/90"
          aria-label={showLabels ? "إخفاء أرقام البلوكات" : "إظهار أرقام البلوكات"}
        >
          
          {showLabels ? "إخفاء الأرقام" : "أرقام البلوكات"}
        </button>
      )}
      {/* هيرو واحد مشترك: يمنع تنزيل/فك ترميز نسختين متنافستين. */}
      <HomeSection first>
        <LabeledBlock blockKey="HomeHero" showLabel={showLabels}>
          <HomeHero initialHero={initialHero} />
        </LabeledBlock>
      </HomeSection>

      {/* الديسكتوب: تخطيط تحريريّ مستقلّ (md: فأعلى) */}
      <HomeDesktopEditorial />

      {/* الموبايل/التابلت: البلوكات كما هي */}
      <div className="md:hidden">
      <HomeSection>
        <BlockDeviceGate blockKey="AboutIntro">
          <LabeledBlock blockKey="AboutIntro" showLabel={showLabels}>
            <HomeBlockOpacityWrap blockKey="AboutIntro">
              <AboutIntro />
            </HomeBlockOpacityWrap>
          </LabeledBlock>
        </BlockDeviceGate>
      </HomeSection>

      <HomeSection>
        <BlockDeviceGate blockKey="AboutIntroTwo">
          <LabeledBlock blockKey="AboutIntroTwo" showLabel={showLabels}>
            <HomeBlockOpacityWrap blockKey="AboutIntroTwo">
              <AboutIntro slot="secondary" />
            </HomeBlockOpacityWrap>
          </LabeledBlock>
        </BlockDeviceGate>
      </HomeSection>

      <HomeSection>
        <BlockDeviceGate blockKey="AboutIntroThree">
          <LabeledBlock blockKey="AboutIntroThree" showLabel={showLabels}>
            <HomeBlockOpacityWrap blockKey="AboutIntroThree">
              <AboutIntro slot="tertiary" />
            </HomeBlockOpacityWrap>
          </LabeledBlock>
        </BlockDeviceGate>
      </HomeSection>

      <HomeSection>
        <React.Suspense fallback={null}><HomeShutterReveal /></React.Suspense>
      </HomeSection>

      <HomeSection>
        <BlockDeviceGate blockKey="HomeSoftBanner">
          <LabeledBlock blockKey="HomeSoftBanner" showLabel={showLabels}>
            <HomeBlockOpacityWrap blockKey="HomeSoftBanner">
              <React.Suspense fallback={null}><HomeSoftBanner /></React.Suspense>
            </HomeBlockOpacityWrap>
          </LabeledBlock>
        </BlockDeviceGate>
      </HomeSection>

      <HomeSection>
        <LabeledBlock blockKey="HomeSixGrid" showLabel={showLabels}>
          <HomeBlockOpacityWrap blockKey="HomeSixGrid">
            <React.Suspense fallback={null}><HomeSixGrid /></React.Suspense>
          </HomeBlockOpacityWrap>
        </LabeledBlock>
      </HomeSection>

      <HomeSection>
        <BlockDeviceGate blockKey="HomeEditorialNote">
          <LabeledBlock blockKey="HomeEditorialNote" showLabel={showLabels}>
            <HomeBlockOpacityWrap blockKey="HomeEditorialNote">
              <React.Suspense fallback={null}><HomeEditorialNote /></React.Suspense>
            </HomeBlockOpacityWrap>
          </LabeledBlock>
        </BlockDeviceGate>
      </HomeSection>

      <HomeSection>
        <LabeledBlock blockKey="HomeQuoteVideoBlock" showLabel={showLabels}>
          <HomeBlockOpacityWrap blockKey="HomeQuoteVideoBlock">
            <React.Suspense fallback={null}><HomeQuoteVideoBlock /></React.Suspense>
          </HomeBlockOpacityWrap>
        </LabeledBlock>
      </HomeSection>

      <HomeSection>
        <BlockDeviceGate blockKey="HomeStoryBanner">
          <LabeledBlock blockKey="HomeStoryBanner" showLabel={showLabels}>
            <HomeBlockOpacityWrap blockKey="HomeStoryBanner">
              <React.Suspense fallback={null}><HomeStoryBanner /></React.Suspense>
            </HomeBlockOpacityWrap>
          </LabeledBlock>
        </BlockDeviceGate>
      </HomeSection>

      <HomeSection>
        <LabeledBlock blockKey="NemroQuoteBlock" showLabel={showLabels}>
          <HomeBlockOpacityWrap blockKey="NemroQuoteBlock">
            <NemroQuoteBlock />
          </HomeBlockOpacityWrap>
        </LabeledBlock>
      </HomeSection>
      </div>

    </>
  );
}



function useHomeTexts() {
  const fetchTexts = useServerFn(getHomeTexts);
  const { data } = useQuery({
    queryKey: ["home-texts"],
    queryFn: () => fetchTexts({ data: {} }),
    staleTime: 60_000,
  });
  return data?.texts ?? null;
}

function AboutIntro({
  slot = "primary",
}: {
  slot?: "primary" | "secondary" | "tertiary";
}) {

  const { locale } = useLocale();
  const isAr = locale === "ar";

  const fetchBlock = useServerFn(getHomeAboutBlock);
  const { data } = useQuery({
    queryKey: ["home-about-block", slot],
    // فشل الجلب (مثلاً 500 من نسخة معاينة قديمة) يجب ألّا يُسقط الصفحة:
    // نُعيد null فيُخفى البلوك بدل كسر الشاشة.
    queryFn: () =>
      fetchBlock({ data: { slot } }).catch((err) => {
        console.warn("[home-about-block] fetch failed:", err);
        return null;
      }),
    retry: 1,
    staleTime: 60_000,
  });
  const cfg = data?.block;
  const pick = (ar?: string, en?: string) =>
    cleanBlockText((isAr ? ar || en : en || ar) ?? "");

  // لا نصوص افتراضيّة: أيّ حقل فارغ في الأدمن يبقى مخفيّاً
  const eyebrow = pick(cfg?.eyebrowAr, cfg?.eyebrowEn);
  const title = pick(cfg?.titleAr, cfg?.titleEn);
  const body = pick(cfg?.bodyAr, cfg?.bodyEn);
  const cta = pick(cfg?.ctaLabelAr, cfg?.ctaLabelEn);

  const ctaHref = (cfg?.ctaHref || "").trim();
  const bgColor = (cfg?.bgColor || "").trim();
  const imageUrl = (cfg?.imageUrl || "").trim();
  const colorOnly = !imageUrl;
  const hasCta = cta.length > 0;
  const hasTitle = title.length > 0;
  const hasBody = body.length > 0;
  const hasEyebrow = eyebrow.length > 0;

  if (cfg && !cfg.enabled) return null;
  if (!hasTitle && !hasBody && !hasCta && !hasEyebrow && !imageUrl && !bgColor) return null;


  const posCfg = TEXT_POSITION_CLASSES[normalizeTextPosition(cfg?.textPosition)];

  const titleEl = hasTitle && (() => {
    const parts = title.trim().split(/\s+/);
    const first = parts[0] ?? "";
    const rest = parts.slice(1).join(" ");
    return (
      <h2
        className={`w-full min-w-0 max-w-full break-words font-serif font-normal uppercase sm:max-w-[760px] ${posCfg.text}`}
        style={{ textWrap: "balance" }}
      >
        <span className="block text-[clamp(42px,6vw,76px)] leading-[0.98] tracking-[-0.03em]">
          {first}
        </span>
        {rest && (
          <span className="mt-2 block text-[clamp(15px,1.9vw,24px)] leading-[1.5] tracking-[0.06em] opacity-70">
            {rest}
          </span>
        )}
      </h2>
    );
  })();

  const bodyEl = hasBody && (
    <p
      className={`mt-[clamp(28px,4vw,56px)] w-full min-w-0 max-w-full whitespace-pre-line break-words text-[13.5px] leading-[1.85] sm:max-w-[620px] md:text-[14.5px] ${posCfg.text}`}
      style={{ fontFamily: isAr ? "var(--font-arabic)" : "var(--font-sans)" }}
    >
      {renderBoldText(body)}
    </p>
  );


  const ctaEl = hasCta && ctaHref && (
    <div className="mt-[clamp(28px,4vw,48px)]">
      <Link to={ctaHref as never} className={softCtaClass}>
        {cta}
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 rtl:-scale-x-100" />
      </Link>
    </div>
  );

  // نمط واحد فقط: وسائط + نصّ فوقها، أو نصّ وحده (بلا صورة)
  return (
    <section
      data-block-bg={colorOnly && bgColor ? bgColor : undefined}
      className={`relative flex min-h-[78svh] flex-col overflow-hidden border-b border-border sm:min-h-[60svh] ${posCfg.container} ${
        colorOnly ? "text-foreground" : "text-background"
      }`}
      style={colorOnly && bgColor ? { backgroundColor: bgColor } : undefined}
    >
      {colorOnly ? null : (
        <AutoMedia
          src={imageUrl}
          alt={
            hasTitle
              ? title
              : isAr
                ? "أجواء بيت عطور ليكمو"
                : "Lecmo perfume house editorial imagery"
          }
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div
        className={`relative z-10 flex w-full max-w-[1100px] flex-col px-6 py-[clamp(64px,9vw,120px)] sm:px-8 ${posCfg.item} ${posCfg.pad}`}
      >
        {hasEyebrow && (
          <span
            className={`mb-6 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] ${
              colorOnly ? "text-foreground/70" : "text-background/80"
            }`}
          >
            <span className="h-px w-8 bg-current opacity-40" aria-hidden="true" />
            {eyebrow}
            <span className="h-px w-8 bg-current opacity-40" aria-hidden="true" />
          </span>
        )}
        {titleEl}
        {bodyEl}
        {ctaEl}
      </div>
    </section>
  );
}



/** Nemro-style centered testimonial / quote block. */
function NemroQuoteBlock() {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const texts = useHomeTexts();
  if (texts && !texts.quoteEnabled) return null;
  const quote =
    (isAr ? texts?.quoteAr || texts?.quoteEn : texts?.quoteEn || texts?.quoteAr) ||
    (isAr
      ? "العطر يجب أن يكون شخصيّاً، معتاداً، ومملوكاً لك بالكامل."
      : "Fragrance should feel personal, lived in and entirely your own.");
  return (
    <section className="border-b border-foreground bg-secondary text-foreground">
      <div className="mx-auto max-w-[1100px] px-6 py-[clamp(90px,14vw,200px)] text-center">
        <blockquote className="m-0 font-serif font-normal leading-[1.12] text-[clamp(30px,5vw,68px)]">
          "{quote}"
        </blockquote>
        <p className="mt-8 text-[10px] uppercase tracking-[0.16em] text-foreground/60">
          — {texts?.quoteAuthor || "LECMO"}
        </p>
      </div>
    </section>
  );
}


